import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IGasLessTxInfo } from '@services/networks/networks.service';
import { ePlatforms } from '@common/networks';

import { StorageService } from '../storage/storage.service';
import { IBlockchain } from './blockchain';

export interface IToken
{
    id?: number;
    blockchain?: number;
    symbol: string;
    name: string;
    address?: string;
    icon?: string;
    common?: boolean;
    blockchainMain?: IBlockchain;
    potential_scam?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class TokenApi
{

    constructor(
        private storage: StorageService,
        private http: HttpClient
    ) { }

    //Gets all Tokens
    public async get(): Promise<IToken[]>
    {
        try
        {
            return <IToken[]>await this.http.get('/api/token').toPromise();
        }
        catch (error)
        {
            return [];
        }
    }

    public async getCoinByBlockchainId(_blockchainId: number[]): Promise<IToken[]>
    {
        try
        {
            return <IToken[]>await this.http.get(`/api/token/coin-by-blockchain-id/${ encodeURIComponent(JSON.stringify(_blockchainId)) }`).toPromise();
        }
        catch (error)
        {
            return [];
        }
    }

    public async getTokenByBlockchainIdAndAddress(_blockchainId: number, _address: string): Promise<IToken>
    {
        try
        {
            return <IToken>await this.http.get(`/api/token/token-by-blockchain-id/${ _blockchainId }/${ _address }`).toPromise();
        }
        catch (error)
        {
            return;
        }
    }
    public async getAllTokenByBlockchainIdAndAddress(_blockchainId: number, _address: string[]): Promise<IToken[]>
    {
        try
        {
            return await this.http.post<IToken[]>(`/api/token/get-by-blockchain-and-addresses`, {
                blockchainId: _blockchainId,
                address: _address
            }).toPromise();
        }
        catch (error)
        {
            return [];
        }
    }

    public async getByBlockchainId(blockchainId: number[]): Promise<IToken[]>
    {
        try
        {
            return await this.http
                .get<IToken[]>(`/api/token/by-blockchain-id/${ encodeURIComponent(JSON.stringify(blockchainId)) }`).toPromise();
        }
        catch (error)
        {
            return [];
        }
    }

    public async getWalletTokensByBlockchainId(_cryptoWalletId: string, _platform: string, _blockchainId: number[]): Promise<IToken[]>
    {
        try
        {
            type tTokenMain = { tokenMain: IToken }[];
            const walletTokens = await this.http.get<tTokenMain>(`/api/wallet-token/by-blockchain-id/${ _cryptoWalletId }/${ _platform }/${ encodeURIComponent(JSON.stringify(_blockchainId)) }`).toPromise();
            return walletTokens.map(({ tokenMain }) => tokenMain);
        }
        catch (error)
        {
            return [];
        }
    }

    public async getWalletTokensByBlockchainIdAll(walletID: number, platform: string, blockchainId: number[]): Promise<any[]>
    {
        try
        {
            const url = `/api/wallet-token/by-blockchain-id/all/${ walletID }/${ platform }/${ encodeURIComponent(JSON.stringify(blockchainId)) }`;
            const currencies = await this.http.get<any[]>(url).toPromise();
            return currencies.map((item) => ({ ...item.tokenMain, walletID: item.wallet }));
        }
        catch (error)
        {
            return [];
        }
    }

    public async getWalletTokens(_cryptoWalletId: string, _platform: string): Promise<IToken[]>
    {
        try
        {
            const walletTokens = <{ tokenMain: IToken; }[]>await this.http.get(`/api/wallet-token/${ _cryptoWalletId }/${ _platform }`).toPromise();

            return walletTokens.map(({ tokenMain }) => tokenMain);
        }
        catch (error)
        {
            return [];
        }
    }

    public async createBaseWalletCurrencies(
        platform: string,
        blockchainId: number[],
        walletID: number
    ): Promise<any[]>
    {
        try
        {
            const walletTokens = await this.http.post<any[]>(`/api/wallet-token/create-base-currencies`, {
                platform,
                blockchainId,
                walletID,
            }).toPromise();

            return walletTokens.map((item) => ({ ...item.tokenMain, walletID: item.wallet }));
        } catch (error)
        {
            return [];
        }
    }

    public async setWalletToken(walletID: number, tokenId: number): Promise<void>
    {
        try
        {
            return await this.http.post<void>(`/api/wallet-token/add`, {
                wallet: walletID,
                tokenId
            }).toPromise();
        } catch (error)
        {
            return undefined;
        }
    }
    public async removeWalletToken(walletID: number, tokenId: number, platform: ePlatforms): Promise<void>
    {
        try
        {
            const url = `/api/wallet-token/remove/${ walletID }/${ tokenId }/${ platform }`;
            return await this.http.delete<void>(url).toPromise();
        }
        catch (error)
        {
            return undefined;
        }
    }


    public async add(
        name: string,
        symbol: string,
        address: string,
        blockchainId: number,
        common?: boolean,
        icon?: string,
        decimals?: number
    ): Promise<IToken>
    {
        const body: any =
        {
            token: {
                name,
                symbol,
                address,
                blockchain: blockchainId,
                icon,
                decimals,
                common,
            },
        };

        try
        {
            const result = await this.http.post<IToken>(`/api/token/add`, body).toPromise();
            console.log(result);

            return result;
        }
        catch (error)
        {
            return undefined;
        }
    }

    public async delete(tokenId: number)
    {
        try
        {
            const result = await this.http.post(`/api/token/remove`, { token: tokenId }).toPromise();
            return result;
        }
        catch (error)
        {
            console.error(error);
        }
    }

    public async supportsGasLess(tokenId: number): Promise<IGasLessTxInfo>
    {
        try
        {
            const supports = await this.http.post('/api/token/supports-gasless', { id: tokenId }).toPromise() as Promise<IGasLessTxInfo>;
            return supports;
        }
        catch (e)
        {
            console.error(e);
            return {
                id: tokenId,
                paymaster: null,
                supports: false
            };
        }
    }

    //   public async createBaseWalletCurrencies(_cryptoWalletId: string, _walletAddress: string, _platform: string) {
    //     try {
    //       const walletCurrencies = <{currency: any}[]>await this.http.post(`/api/wallet-currency/create-base-currencies`, {
    //         cryptoWalletId: _cryptoWalletId,
    //         walletAddress: _walletAddress,
    //         platform: _platform
    //       }).toPromise();

    //       return walletCurrencies.map(({currency}) => currency);
    //     } catch (error) {
    //       return undefined;
    //     }
    //   }
}
